/**
 * The flashlight, as an object in the world.
 *
 * Shared by your own torch and by every teammate's, because they are the same
 * thing seen from different places and had drifted apart while they were two
 * pieces of code. The light belongs to the casing rather than to a camera: a
 * beam that leaves the viewer instead of the hand holding it has to be
 * compensated for every time the camera moves, and gets it wrong the moment
 * anything forgets to.
 *
 * Everything here is laid out down local -Z, which is the beam direction, so a
 * rig is aimed by rotating it rather than by recomputing a target.
 */

import * as THREE from 'three';
import { PLAYER } from '@static-pines/shared';

/** Casing dimensions, in metres. A torch is a small thing in a big forest. */
export const TORCH_LENGTH = 0.2;
export const TORCH_RADIUS = 0.035;

/**
 * Where a carried torch sits, relative to the feet of whoever holds it.
 *
 * Held out in front and off to one side, in a hand — not on the body's centre
 * line, where the lens and its halo end up buried inside a 0.3m-radius torso
 * and the light appears to come out of the ribcage.
 *
 * The lateral and vertical offsets are the ones the first-person view has
 * always used against its camera (0.28 right, 0.18 below eye level), so your
 * beam and a teammate's leave the hand from the same place.
 */
export const HELD_TORCH = { x: 0.28, y: PLAYER.EYE_HEIGHT - 0.18, z: -0.34 };

/**
 * Lever arm for the beam's aim point.
 *
 * A SpotLight only cares about the direction from itself to its target, so the
 * distance is arbitrary; it just needs to be long enough that rounding cannot
 * wobble the heading.
 */
export const TORCH_AIM_DISTANCE = 4;

/**
 * The casing, its lit lens, and the halo that carries it at range.
 *
 * Visuals only — no light. The SpotLight is added alongside this by whoever
 * builds the rig, because hiding a group in three.js also stops its lights
 * being collected, and the casing has to be hideable in first person without
 * the torch going out.
 */
export function makeTorchModel(): THREE.Group {
  const group = new THREE.Group();

  const casing = new THREE.Mesh(
    new THREE.CylinderGeometry(TORCH_RADIUS, TORCH_RADIUS * 0.85, TORCH_LENGTH, 8),
    new THREE.MeshLambertMaterial({ color: 0x2b3138 }),
  );
  // Cylinders stand along Y; lay this one down the beam.
  casing.rotation.x = -Math.PI / 2;
  casing.position.z = -TORCH_LENGTH / 2;
  group.add(casing);

  /**
   * The lens, unlit on purpose.
   *
   * MeshBasicMaterial ignores scene lighting, which is exactly right for the
   * one surface in the scene that is emitting rather than receiving. A Lambert
   * lens would be lit by the very torch it belongs to and come out black.
   */
  const lens = new THREE.Mesh(
    new THREE.CircleGeometry(TORCH_RADIUS * 1.05, 12),
    new THREE.MeshBasicMaterial({ color: 0xfff1d4 }),
  );
  lens.position.z = -TORCH_LENGTH;
  // Circles face +Z as built; turn it to look down the beam.
  lens.rotation.y = Math.PI;
  group.add(lens);

  const glow = makeTorchGlow();
  glow.position.z = -TORCH_LENGTH - 0.02;
  group.add(glow);

  return group;
}

/**
 * The halo that makes a torch findable at range.
 *
 * The lens is a 7cm disc: past thirty metres or so it is sub-pixel and simply
 * stops existing, which is the distance at which finding your team actually
 * matters. A camera-facing sprite holds a constant world size, so it stays a
 * few pixels of warm light however far off it is — and being a sprite it also
 * reads from behind and off to the side, where the flat lens would be turned
 * away or backface-culled.
 */
function makeTorchGlow(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;

  // A hot core falling away to nothing — the profile of a bare bulb.
  const halo = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  halo.addColorStop(0, 'rgba(255, 248, 230, 1)');
  halo.addColorStop(0.18, 'rgba(255, 236, 200, 0.85)');
  halo.addColorStop(0.5, 'rgba(255, 214, 150, 0.28)');
  halo.addColorStop(1, 'rgba(255, 200, 120, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, 64, 64);

  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    /**
     * Fog is off for this one thing.
     *
     * Everything else in the forest is meant to dissolve into the murk, but a
     * light source is what fog scatters rather than swallows — headlights in
     * mist read as brighter haloes, not dimmer ones. Fogged, the halo faded to
     * nothing by about sixty metres and took the whole point of it with it.
     * Its small fixed size is what keeps it a pinprick at range.
     */
    fog: false,
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(0.42, 0.42, 1);
  return sprite;
}

/** Free the geometry, materials and textures a torch model owns. */
export function disposeTorchModel(model: THREE.Group): void {
  model.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      (object.material as THREE.Material).dispose();
    } else if (object instanceof THREE.Sprite) {
      object.material.map?.dispose();
      object.material.dispose();
    }
  });
}
